import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { loadStoredBrandConfig } from "../config/brand.js";
import {
  contactContent,
  PREFERRED_CONTACT_OPTIONS,
  SERVICE_OPTIONS,
  TIMELINE_OPTIONS,
} from "../content/contact.js";
import {
  parseInquiryErrors,
  submitContactInquiry,
} from "../services/inquiries.js";
import { LOCAL_BUSINESS } from "../config/seo.js";

const AI_SERVICE_IDS = new Set(["receptionist", "ai-chat"]);
const VALID_SERVICE_IDS = new Set(SERVICE_OPTIONS.map((s) => s.id));

const EMPTY_FORM = {
  name: "",
  business_name: "",
  email: "",
  phone: "",
  services: [],
  message: "",
  interested_support: false,
  consent_marketing: false,
  consent_ai_recording: false,
  consent_sms: false,
  preferred_contact: "either",
  timeline: "",
  website: "",
};

function validateClient(form) {
  /** @type {Record<string, string>} */
  const errors = {};
  if (!form.name.trim() || form.name.trim().length < 2) {
    errors.name = "Please enter your name.";
  }
  if (!form.business_name.trim() || form.business_name.trim().length < 2) {
    errors.business_name = "Please enter your business name.";
  }
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
    errors.email = "Please enter a valid email address.";
  }
  if (form.phone.trim() && !/^[\d\s\-+().]{7,}$/.test(form.phone.trim())) {
    errors.phone = "Please enter a valid phone number.";
  } else if (!form.phone.trim()) {
    // Cross-field: a phone number is required when the visitor asks to be
    // reached by phone or opts into SMS — either is meaningless without one.
    if (form.preferred_contact === "phone") {
      errors.phone = "Add a phone number, or change your preferred contact method.";
    } else if (form.consent_sms) {
      errors.phone = "Add a phone number to receive SMS updates, or uncheck SMS.";
    }
  }
  if (!form.services.length) {
    errors.services = "Select at least one service.";
  }
  if (!form.message.trim() || form.message.trim().length < 10) {
    errors.message = "Please share a bit more detail (at least 10 characters).";
  }
  const needsAiConsent = form.services.some((id) => AI_SERVICE_IDS.has(id));
  if (needsAiConsent && !form.consent_ai_recording) {
    errors.consent_ai_recording =
      "AI receptionist and AI chat require consent for AI-assisted communication.";
  }
  return errors;
}

export default function ContactPage() {
  const config = loadStoredBrandConfig();
  const c = contactContent;
  const [searchParams] = useSearchParams();

  const initialServices = useMemo(() => {
    const raw = searchParams.get("service");
    if (raw && VALID_SERVICE_IDS.has(raw)) {
      return [raw];
    }
    return [];
  }, [searchParams]);

  const [form, setForm] = useState({ ...EMPTY_FORM, services: initialServices });
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const setField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[name];
      // The phone error can be driven by these two fields (cross-field rule), so
      // changing them should also dismiss a stale phone requirement error.
      if (name === "preferred_contact" || name === "consent_sms") {
        delete next.phone;
      }
      return next;
    });
  };

  const toggleService = (id) => {
    setForm((prev) => {
      const has = prev.services.includes(id);
      const services = has
        ? prev.services.filter((s) => s !== id)
        : [...prev.services, id];
      return { ...prev, services };
    });
    setErrors((prev) => {
      const next = { ...prev };
      delete next.services;
      return next;
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    if (form.website.trim()) {
      setSuccess(true);
      return;
    }

    const clientErrors = validateClient(form);
    if (Object.keys(clientErrors).length) {
      setErrors(clientErrors);
      return;
    }

    setSubmitting(true);
    try {
      await submitContactInquiry({
        name: form.name.trim(),
        business_name: form.business_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        services: form.services,
        message: form.message.trim(),
        interested_support: form.interested_support,
        consent_marketing: form.consent_marketing,
        consent_ai_recording: form.consent_ai_recording,
        consent_sms: form.consent_sms,
        preferred_contact: form.preferred_contact,
        timeline: form.timeline,
        website: form.website,
      });
      setSuccess(true);
    } catch (err) {
      const parsed = parseInquiryErrors(err);
      const { _form, ...fieldErrors } = parsed;
      setErrors(fieldErrors);
      setFormError(_form || "");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setForm({ ...EMPTY_FORM, services: [] });
    setErrors({});
    setFormError("");
    setSuccess(false);
  };

  if (success) {
    return (
      <article className="site-page contact-page">
        <div className="site-page__inner site-page__inner--wide contact-page__inner">
          <div className="contact-success" role="status">
            <h1 className="site-page__title">{c.success.title}</h1>
            <p className="site-page__lead">{c.success.body}</p>
            <p className="site-page__cta">
              <button
                type="button"
                className="site-button-primary"
                onClick={resetForm}
              >
                {c.success.again}
              </button>
              <Link to="/" className="site-button-ghost">
                ← Back to home
              </Link>
            </p>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="site-page contact-page">
      <div className="site-page__inner site-page__inner--wide contact-page__inner">
        <div className="contact-layout">
          <aside className="contact-aside">
            <p className="site-page__eyebrow">{c.eyebrow}</p>
            <h1 className="site-page__title contact-hero__title">{c.title}</h1>
            <p className="site-page__lead contact-hero__lead">{c.lead}</p>
            <p className="site-page__note">{config.identity.footerNote}</p>

            <address className="contact-nap">
              <span className="contact-nap__name">{LOCAL_BUSINESS.name}</span>
              {LOCAL_BUSINESS.streetAddress}
              <br />
              {LOCAL_BUSINESS.addressLocality}, {LOCAL_BUSINESS.addressRegion}{" "}
              {LOCAL_BUSINESS.postalCode}
              <br />
              <a href={`tel:${LOCAL_BUSINESS.telephone}`}>
                {LOCAL_BUSINESS.phoneDisplay}
              </a>
            </address>

            <div className="contact-upsell">
              <h2 className="contact-upsell__title">{c.supportUpsell.title}</h2>
              <p className="contact-upsell__body">{c.supportUpsell.body}</p>
              <ul className="contact-upsell__list">
                <li>UniFi, web, and AI under one support desk</li>
                <li>Ticket portal for status and next steps</li>
                <li>Priority response options for critical sites</li>
              </ul>
              <Link to="/unifi-networks" className="contact-upsell__link">
                See network support →
              </Link>
            </div>
          </aside>

          <div className="contact-form-panel">
            <form className="contact-form" onSubmit={onSubmit} noValidate>
              <input
                type="text"
                name="website"
                className="contact-form__honeypot"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                value={form.website}
                onChange={(e) => setField("website", e.target.value)}
              />

              {formError ? (
                <p className="contact-form__banner contact-form__banner--error" role="alert">
                  {formError}
                </p>
              ) : null}

              <fieldset className="contact-form__fieldset">
                <legend className="contact-form__legend">
                  What can we help with? <span className="contact-form__req">*</span>
                </legend>
                <div className="contact-form__service-grid">
                  {SERVICE_OPTIONS.map((opt) => {
                    const checked = form.services.includes(opt.id);
                    return (
                      <label
                        key={opt.id}
                        className={`contact-form__service${checked ? " is-selected" : ""}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleService(opt.id)}
                        />
                        <span className="contact-form__service-text">
                          <span className="contact-form__service-label">
                            {opt.label}
                          </span>
                          <span className="contact-form__service-desc">
                            {opt.description}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </div>
                {errors.services ? (
                  <p className="contact-form__error" id="err-services">
                    {errors.services}
                  </p>
                ) : null}
              </fieldset>

              <div className="contact-form__row">
                <div className="contact-form__field">
                  <label htmlFor="contact-name">
                    Your name <span className="contact-form__req">*</span>
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    autoComplete="name"
                    value={form.name}
                    onChange={(e) => setField("name", e.target.value)}
                    aria-invalid={!!errors.name}
                    aria-describedby={errors.name ? "err-name" : undefined}
                  />
                  {errors.name ? (
                    <p className="contact-form__error" id="err-name">
                      {errors.name}
                    </p>
                  ) : null}
                </div>

                <div className="contact-form__field">
                  <label htmlFor="contact-business">
                    Business <span className="contact-form__req">*</span>
                  </label>
                  <input
                    id="contact-business"
                    name="business_name"
                    type="text"
                    autoComplete="organization"
                    value={form.business_name}
                    onChange={(e) => setField("business_name", e.target.value)}
                    aria-invalid={!!errors.business_name}
                    aria-describedby={
                      errors.business_name ? "err-business" : undefined
                    }
                  />
                  {errors.business_name ? (
                    <p className="contact-form__error" id="err-business">
                      {errors.business_name}
                    </p>
                  ) : null}
                </div>
              </div>

              <div className="contact-form__row">
                <div className="contact-form__field">
                  <label htmlFor="contact-email">
                    Email <span className="contact-form__req">*</span>
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={form.email}
                    onChange={(e) => setField("email", e.target.value)}
                    aria-invalid={!!errors.email}
                    aria-describedby={errors.email ? "err-email" : undefined}
                  />
                  {errors.email ? (
                    <p className="contact-form__error" id="err-email">
                      {errors.email}
                    </p>
                  ) : null}
                </div>

                <div className="contact-form__field">
                  <label htmlFor="contact-phone">Phone</label>
                  <input
                    id="contact-phone"
                    name="phone"
                    type="tel"
                    autoComplete="tel"
                    value={form.phone}
                    onChange={(e) => setField("phone", e.target.value)}
                    aria-invalid={!!errors.phone}
                    aria-describedby={errors.phone ? "err-phone" : undefined}
                  />
                  {errors.phone ? (
                    <p className="contact-form__error" id="err-phone">
                      {errors.phone}
                    </p>
                  ) : null}
                </div>
              </div>

              <div className="contact-form__row">
                <div className="contact-form__field">
                  <label htmlFor="contact-pref">Preferred contact</label>
                  <select
                    id="contact-pref"
                    value={form.preferred_contact}
                    onChange={(e) => setField("preferred_contact", e.target.value)}
                  >
                    {PREFERRED_CONTACT_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="contact-form__field">
                  <label htmlFor="contact-timeline">Timeline</label>
                  <select
                    id="contact-timeline"
                    value={form.timeline}
                    onChange={(e) => setField("timeline", e.target.value)}
                  >
                    {TIMELINE_OPTIONS.map((o) => (
                      <option key={o.value || "blank"} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="contact-form__field">
                <label htmlFor="contact-message">
                  Message <span className="contact-form__req">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  rows={5}
                  value={form.message}
                  onChange={(e) => setField("message", e.target.value)}
                  aria-invalid={!!errors.message}
                  aria-describedby={errors.message ? "err-message" : undefined}
                  placeholder="What are you trying to accomplish? Timelines, locations, and current setup help."
                />
                {errors.message ? (
                  <p className="contact-form__error" id="err-message">
                    {errors.message}
                  </p>
                ) : null}
              </div>

              <label className="contact-form__check contact-form__check--upsell">
                <input
                  type="checkbox"
                  checked={form.interested_support}
                  onChange={(e) => setField("interested_support", e.target.checked)}
                />
                <span>{c.supportUpsell.checkboxLabel}</span>
              </label>

              <div className="contact-form__consents">
                <label className="contact-form__check">
                  <input
                    type="checkbox"
                    checked={form.consent_marketing}
                    onChange={(e) =>
                      setField("consent_marketing", e.target.checked)
                    }
                  />
                  <span>{c.consents.marketing}</span>
                </label>

                <label className="contact-form__check">
                  <input
                    type="checkbox"
                    checked={form.consent_ai_recording}
                    onChange={(e) =>
                      setField("consent_ai_recording", e.target.checked)
                    }
                    aria-invalid={!!errors.consent_ai_recording}
                    aria-describedby={
                      errors.consent_ai_recording ? "err-ai-consent" : undefined
                    }
                  />
                  <span>
                    {c.consents.aiRecording}
                    {form.services.some((id) => AI_SERVICE_IDS.has(id)) ? (
                      <span className="contact-form__req"> *</span>
                    ) : null}
                  </span>
                </label>
                {errors.consent_ai_recording ? (
                  <p className="contact-form__error" id="err-ai-consent">
                    {errors.consent_ai_recording}
                  </p>
                ) : null}

                {/* SMS opt-in: grouped with the other consents, full-width,
                    unchecked by default. Submitting the form does NOT imply SMS
                    consent — this explicit checkbox is the only way to opt in,
                    and it stays separate from marketing and AI-recording. */}
                <label className="contact-form__check contact-form__check--sms">
                  <input
                    type="checkbox"
                    name="consent_sms"
                    checked={form.consent_sms}
                    onChange={(e) => setField("consent_sms", e.target.checked)}
                  />
                  <span>
                    {c.consents.sms}
                    <span className="contact-form__sms-links">
                      <Link to="/privacy">Privacy Policy</Link>
                      {" · "}
                      <Link to="/terms">Terms and Conditions</Link>
                    </span>
                  </span>
                </label>
              </div>

              <p className="contact-form__fine">
                By submitting, you agree we may store this information to respond
                to your inquiry. We do not sell your contact details.
              </p>

              <button
                type="submit"
                className="site-button-primary contact-form__submit"
                disabled={submitting}
              >
                {submitting ? c.submittingLabel : c.submitLabel}
              </button>
            </form>
          </div>
        </div>
      </div>
    </article>
  );
}
